import { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';

interface DocumentCaptureProps {
  title: string;
  instruction: string;
  onCapture: (imageBlob: Blob) => Promise<void>;
  isLoading: boolean;
}

export function DocumentCapture({
  title,
  instruction,
  onCapture,
  isLoading
}: DocumentCaptureProps) {
  const webcamRef = useRef<Webcam>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const capture = useCallback(() => {
    setIsCapturing(true);

    setTimeout(() => {
      // Capturar SIN forzar dimensiones - usa la resolución nativa del video
      // Esto evita distorsiones en móvil
      const imageSrc = webcamRef.current?.getScreenshot();
      if (imageSrc) {
        setPreview(imageSrc);
      }
      setIsCapturing(false);
    }, 200);
  }, []);

  const confirmCapture = async () => {
    if (!preview) return;

    // Convertir base64 a Blob
    const response = await fetch(preview);
    const blob = await response.blob();

    await onCapture(blob);
  };

  const retake = () => {
    setPreview(null);
  };

  const handleCameraError = (error: string | DOMException) => {
    console.error('Camera error:', error);
    setCameraError('No se pudo acceder a la cámara. Por favor, permite el acceso.');
  };

  if (cameraError) {
    return (
      <div className="text-center space-y-4 py-8">
        <div className="text-5xl">📷</div>
        <h3 className="text-lg font-semibold text-red-600">Error de cámara</h3>
        <p className="text-gray-600">{cameraError}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center p-4 min-h-[60vh]">
      <div className="w-full max-w-md md:max-w-2xl mx-auto space-y-3 md:space-y-4">
        <div className="text-center">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 text-xs md:text-sm mt-0.5 md:mt-1">{instruction}</p>
        </div>

      <div className="relative rounded-2xl overflow-hidden bg-black shadow-lg md:shadow-xl" style={{ aspectRatio: '4/3' }}>
        {!preview ? (
          <>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              screenshotQuality={0.92}
              onUserMediaError={handleCameraError}
              videoConstraints={{
                facingMode: { ideal: 'environment' },
              }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />

            {/* Guía visual */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-[85%] h-[60%]">
                {/* Marco */}
                <div className="absolute inset-0 border-4 border-white/70 rounded-lg" />

                {/* Esquinas */}
                <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-sky-400 rounded-tl-lg" />
                <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-sky-400 rounded-tr-lg" />
                <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-sky-400 rounded-bl-lg" />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-sky-400 rounded-br-lg" />
              </div>
            </div>

            {/* Flash effect */}
            {isCapturing && (
              <div className="absolute inset-0 bg-white animate-pulse" />
            )}
          </>
        ) : (
          <img
            src={preview}
            alt="Captura"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>

      {/* Instrucciones adicionales compactas */}
      <div className="flex gap-3 md:gap-6 justify-center text-[10px] md:text-sm text-gray-500">
        <span className="flex items-center gap-0.5 md:gap-1">
          <span>💡</span> Buena luz
        </span>
        <span className="flex items-center gap-0.5 md:gap-1">
          <span>📐</span> Sin reflejos
        </span>
        <span className="flex items-center gap-0.5 md:gap-1">
          <span>🎯</span> Enfocado
        </span>
      </div>

      {/* Botones GRANDES para móvil y desktop */}
      <div className="flex gap-3 md:gap-4">
        {!preview ? (
          <button
            onClick={capture}
            disabled={isCapturing}
            className="flex-1 py-4 md:py-5 px-6 md:px-8 rounded-xl font-bold text-white text-base md:text-lg
                       bg-gradient-to-r from-sky-500 to-blue-600
                       hover:from-sky-600 hover:to-blue-700
                       disabled:opacity-50 transition-all duration-200
                       shadow-lg hover:shadow-xl"
          >
            {isCapturing ? '📸 Capturando...' : '📷 Capturar'}
          </button>
        ) : (
          <>
            <button
              onClick={retake}
              disabled={isLoading}
              className="flex-1 py-4 md:py-5 px-6 md:px-8 rounded-xl font-bold text-base md:text-lg
                         bg-gray-200 text-gray-700 hover:bg-gray-300
                         disabled:opacity-50 transition-all duration-200"
            >
              🔄 Repetir
            </button>
            <button
              onClick={confirmCapture}
              disabled={isLoading}
              className="flex-1 py-4 md:py-5 px-6 md:px-8 rounded-xl font-bold text-white text-base md:text-lg
                         bg-gradient-to-r from-sky-500 to-blue-600
                         hover:from-sky-600 hover:to-blue-700
                         disabled:opacity-50 transition-all duration-200
                         shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Subiendo...
                </span>
              ) : (
                '✓ Confirmar'
              )}
            </button>
          </>
        )}
      </div>
      </div>
    </div>
  );
}


